/**
 * Cookie consent + Google Consent Mode v2.
 *
 * Consent is stored in a cookie (not localStorage) so that it is readable
 * synchronously by the bootstrap script in `src/html.js`, which has to run
 * before GTM/gtag load. See `getConsentBootstrapScript` below.
 */

export const CONSENT_COOKIE_NAME = 'ph_cookie_consent'
export const CONSENT_COOKIE_MAX_AGE_DAYS = 180
export const CONSENT_VERSION = 1

export const CONSENT_CATEGORIES = {
  ANALYTICS: 'analytics',
  MARKETING: 'marketing',
  FUNCTIONAL: 'functional',
}

// Strictly necessary cookies are not represented here: they are never gated.
export const DENIED_CONSENT = {
  analytics: false,
  marketing: false,
  functional: false,
}

export const GRANTED_CONSENT = {
  analytics: true,
  marketing: true,
  functional: true,
}

// https://developers.google.com/tag-platform/security/guides/consent
export const GOOGLE_CONSENT_KEYS_BY_CATEGORY = {
  analytics: ['analytics_storage'],
  marketing: ['ad_storage', 'ad_user_data', 'ad_personalization'],
  functional: ['functionality_storage', 'personalization_storage'],
}

export const ALWAYS_GRANTED_CONSENT_KEYS = ['security_storage']

/**
 * Translates our category booleans into the granted/denied signals gtag expects.
 */
export const toGoogleConsentSignals = (consent = DENIED_CONSENT) => {
  const signals = {}

  Object.entries(GOOGLE_CONSENT_KEYS_BY_CATEGORY).forEach(
    ([category, keys]) => {
      keys.forEach(key => {
        signals[key] = consent[category] ? 'granted' : 'denied'
      })
    }
  )

  ALWAYS_GRANTED_CONSENT_KEYS.forEach(key => {
    signals[key] = 'granted'
  })

  return signals
}

const parseConsentCookieValue = rawValue => {
  if (!rawValue) return null

  try {
    const parsed = JSON.parse(decodeURIComponent(rawValue))
    // A version bump invalidates stored consent so we re-prompt rather than
    // silently honouring a record written under different categories.
    if (!parsed || parsed.v !== CONSENT_VERSION) return null
    return parsed
  } catch {
    return null
  }
}

export const readConsentCookie = () => {
  if (typeof document === 'undefined') return null

  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE_NAME}=([^;]*)`)
  )

  return parseConsentCookieValue(match && match[1])
}

export const writeConsentCookie = consent => {
  if (typeof document === 'undefined') return

  const value = encodeURIComponent(
    JSON.stringify({ v: CONSENT_VERSION, answered: true, ...consent })
  )
  const maxAge = CONSENT_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''

  document.cookie = `${CONSENT_COOKIE_NAME}=${value}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`
}

/**
 * Fired when the visitor asks to revisit their choice (the footer's "Cookie
 * Settings" link). Withdrawing consent has to be as easy as giving it, so the
 * banner listens for this and re-opens regardless of what was stored.
 */
export const CONSENT_PREFERENCES_EVENT = 'ph:open-cookie-preferences'

export const openConsentPreferences = () => {
  if (typeof window === 'undefined') return

  window.dispatchEvent(new CustomEvent(CONSENT_PREFERENCES_EVENT))
}

/**
 * Pushes a `consent update` so Google tags already on the page react to the
 * user's choice without requiring a reload.
 */
export const updateGoogleConsent = consent => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return

  window.gtag('consent', 'update', toGoogleConsentSignals(consent))
}

/**
 * Inline script injected at the very top of `<head>`, ahead of GTM and gtag.
 *
 * This MUST stay dependency-free: it runs before any bundle is parsed. It
 * denies every non-essential storage type up front, then immediately re-grants
 * whatever a returning visitor previously accepted.
 */
export const getConsentBootstrapScript = () => `
  (function () {
    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = window.gtag || gtag;

    gtag('consent', 'default', Object.assign(
      ${JSON.stringify(toGoogleConsentSignals(DENIED_CONSENT))},
      { wait_for_update: 500 }
    ));
    gtag('set', 'ads_data_redaction', true);
    gtag('set', 'url_passthrough', true);

    try {
      var match = document.cookie.match(
        /(?:^|;\\s*)${CONSENT_COOKIE_NAME}=([^;]*)/
      );
      if (!match) return;

      var stored = JSON.parse(decodeURIComponent(match[1]));
      if (!stored || stored.v !== ${CONSENT_VERSION}) return;

      var keysByCategory = ${JSON.stringify(GOOGLE_CONSENT_KEYS_BY_CATEGORY)};
      var signals = {};
      Object.keys(keysByCategory).forEach(function (category) {
        keysByCategory[category].forEach(function (key) {
          signals[key] = stored[category] ? 'granted' : 'denied';
        });
      });
      ${JSON.stringify(ALWAYS_GRANTED_CONSENT_KEYS)}.forEach(function (key) {
        signals[key] = 'granted';
      });

      gtag('consent', 'update', signals);
    } catch (error) {
      /* Malformed cookie: stay denied. */
    }
  })();
`
